import * as callableModule from './callable'

const callHandler = <Req, Res>(name: string, payload: Req) =>
  callableModule.callable<Req, Res>(name, payload)

export interface SchoolEduScope {
  kind: 'SESSION' | 'EXAM' | 'COACH'
  sessionId?: string
  assessmentId?: string
  hall?: string
  metricDefIds?: string[]
}

export interface CreateSchoolFieldPassInput {
  workspaceId: string
  eduScope: SchoolEduScope
  expiresInHours?: number
  displayName?: string
  targetEmail?: string
}

export interface CreateSchoolFieldPassResult {
  passId: string
  token: string
  shareableUrl: string
  expiresAt: string
}

export interface SubmitFieldPassRollInput {
  passId: string
  marks: Array<{
    learnerId: string
    status: 'PRESENT' | 'ABSENT' | 'LATE' | 'ON_LEAVE'
  }>
}

export interface SubmitFieldPassRollResult {
  success: boolean
  entryId: string
  status: 'PENDING'
}

export interface SubmitFieldPassExamSeatsInput {
  passId: string
  seats: Array<{
    learnerId: string
    seatNo: string
    room?: string
  }>
}

export interface SubmitFieldPassExamSeatsResult {
  success: boolean
  entryId: string
  status: 'PENDING'
}

export interface SubmitFieldPassSessionMetricsInput {
  passId: string
  metrics: Array<{
    learnerId: string
    metricDefId: string
    value: number | string
    notes?: string
  }>
}

export interface SubmitFieldPassSessionMetricsResult {
  success: boolean
  entryId: string
  status: 'PENDING'
}

export interface ReviewFieldPassEntriesInput {
  workspaceId: string
  passId: string
  accept: boolean
  reason?: string
}

export interface ReviewFieldPassEntriesResult {
  success: boolean
  accept: boolean
}

export interface PendingFieldPassEntry {
  id: string
  workspaceId: string
  passId: string
  type: 'ROLL' | 'EXAM_SEATS' | 'SESSION_METRICS'
  eduScope?: SchoolEduScope
  entries: Record<string, unknown>[]
  source: string
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED'
  submittedAt?: unknown
  submittedBy?: string
  displayName?: string
}

export interface ListPendingFieldPassEntriesInput {
  workspaceId: string
  sessionId?: string
}

export interface ListPendingFieldPassEntriesResult {
  entries: PendingFieldPassEntry[]
}

export interface GetFieldPassSessionInput {
  passId: string
}

export interface GetFieldPassSessionResult {
  pass: Record<string, unknown>
  session: Record<string, unknown>
  tasks: Record<string, unknown>[]
  channels: Record<string, unknown>[]
  messagesByChannel: Record<string, unknown[]>
  learners?: Array<{
    learnerId: string
    displayName: string
    rollNo: string | null
  }>
}

export const createSchoolFieldPass = async (
  input: CreateSchoolFieldPassInput,
): Promise<CreateSchoolFieldPassResult> => {
  const res = await callHandler<CreateSchoolFieldPassInput, { pass: { id: string; expiresAt: unknown }; token: string; url: string }>('createFieldPass', input)
  return {
    passId: res.pass.id,
    token: res.token,
    shareableUrl: res.url,
    expiresAt: String(res.pass.expiresAt),
  }
}

export const submitFieldPassRoll = async (
  input: SubmitFieldPassRollInput,
): Promise<SubmitFieldPassRollResult> => {
  return callHandler<SubmitFieldPassRollInput, SubmitFieldPassRollResult>('submitFieldPassRoll', input)
}

export const submitFieldPassExamSeats = async (
  input: SubmitFieldPassExamSeatsInput,
): Promise<SubmitFieldPassExamSeatsResult> => {
  return callHandler<SubmitFieldPassExamSeatsInput, SubmitFieldPassExamSeatsResult>('submitFieldPassExamSeats', input)
}

export const submitFieldPassSessionMetrics = async (
  input: SubmitFieldPassSessionMetricsInput,
): Promise<SubmitFieldPassSessionMetricsResult> => {
  return callHandler<SubmitFieldPassSessionMetricsInput, SubmitFieldPassSessionMetricsResult>('submitFieldPassSessionMetrics', input)
}

export const reviewFieldPassEntries = async (
  input: ReviewFieldPassEntriesInput,
): Promise<ReviewFieldPassEntriesResult> => {
  return callHandler<ReviewFieldPassEntriesInput, ReviewFieldPassEntriesResult>('reviewFieldPassEntries', input)
}

export const listPendingFieldPassEntries = async (
  input: ListPendingFieldPassEntriesInput,
): Promise<ListPendingFieldPassEntriesResult> => {
  return callHandler<ListPendingFieldPassEntriesInput, ListPendingFieldPassEntriesResult>('listPendingFieldPassEntries', input)
}

export const getFieldPassSession = async (
  input: GetFieldPassSessionInput,
): Promise<GetFieldPassSessionResult> => {
  return callHandler<GetFieldPassSessionInput, GetFieldPassSessionResult>('getFieldPassSession', input)
}
